import {
  FileDocumentEditOutline as DescriptionIcon,
  Help as HelpIcon,
  Bell as NotificationsIcon,
  Magnify as SearchIcon,
  Cog as SettingsIcon
} from '@opengov/react-capital-assets';

import { IconButton, SvgIcon, Tooltip } from '@mui/material';
import React from 'react';

import { NAV_COLOR_PALETTE, tooltipOffsetProps } from '../../Components/styleConstants';
import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { BadgeVariant, UtilityNames } from '../../types';
import { getBadgeAccessibilitySuffix } from './BadgeNotification';
import { ConditionalBadgeNotificationRenderer } from './ConditionalBadgeNotificationRenderer';

type UtilityIcons = {
  [UtilityNames.help]: typeof SvgIcon;
  [UtilityNames.notifications]: typeof SvgIcon;
  [UtilityNames.profile]: typeof SvgIcon;
  [UtilityNames.reports]: typeof SvgIcon;
  [UtilityNames.settings]: typeof SvgIcon;
  [UtilityNames.search]: typeof SvgIcon;
};

export const TRAY_BUTTON_ICONS: Partial<UtilityIcons> = {
  Help: HelpIcon,
  Notifications: NotificationsIcon,
  Reports: DescriptionIcon,
  Settings: SettingsIcon,
  Search: SearchIcon
};

export interface ITrayControllerButtonProps {
  utilityName: UtilityNames | string;
  expanded: boolean;
  associatedMenuId: string;
  id: string;
  customMuiIcon?: typeof SvgIcon;
  stringIcon?: string;
  badgeContent?: number;
  badgeVariant?: BadgeVariant;
  onClick(e: React.MouseEvent<HTMLElement>): void;
  onKeyDown?(e: React.KeyboardEvent<HTMLElement>): void;
}

export const TrayControllerButtonBase: React.ForwardRefRenderFunction<HTMLButtonElement, ITrayControllerButtonProps> = (
  {
    expanded,
    utilityName,
    id,
    associatedMenuId,
    customMuiIcon,
    stringIcon,
    onClick,
    onKeyDown,
    badgeContent,
    badgeVariant
  },
  ref
) => {
  const baseLabel = `toggle ${utilityName} menu`;
  const badgeSuffix = getBadgeAccessibilitySuffix(badgeContent, badgeVariant);
  const ariaLabel = `${baseLabel}${badgeSuffix}`;

  return (
    <Tooltip title={utilityName} placement="top" enterNextDelay={500} disableInteractive={true} {...tooltipOffsetProps}>
      <IconButton
        ref={ref}
        sx={{
          color: NAV_COLOR_PALETTE.secondaryGray,
          width: 32,
          height: 32
        }}
        size="small"
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        aria-expanded={expanded}
        // accessibility button/menu relationship IDs
        id={id}
        aria-controls={associatedMenuId}
        data-test={getDynamicDataTestId(DATA_TEST.tray_controller_button, utilityName.toLowerCase())}
        data-og={getDynamicDataOGId(DATA_OG.tray_controller_button, utilityName)}
      >
        <ConditionalBadgeNotificationRenderer
          utilityName={utilityName}
          customMuiIcon={customMuiIcon}
          stringIcon={stringIcon}
          badgeContent={badgeContent}
          badgeVariant={badgeVariant}
        />
      </IconButton>
    </Tooltip>
  );
};

export const TrayControllerButton = React.forwardRef(TrayControllerButtonBase);
